<template>
  <element>
    <div v-if="active" class="overlay"></div>
    <v-menu 
      :close-on-click="closeOnClick"
      :close-on-content-click="closeOnContentClick"
      :value="active"
      :top="!top"
      :bottom="top"
      offset-y
      nudge-width=329
      nudge-height=339
      :nudge-top="top ? -14 : 14"
      eager
      class="feedback-menu">
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          :class="top ? 'mt-5' : ''"
          color="#D9753B"
          fixed
          fab
          dark
          :top="top"
          :bottom="!top"
          :right="!left"
          :left="left"
          v-bind="attrs"
          v-on="on"
          @click="onClick"
        >
          <v-icon v-if="active" dark>mdi-minus</v-icon>
          <v-icon class="h-flip" v-else dark>mdi-message-plus</v-icon>
        </v-btn>
      </template>

      <v-card class="p-3 feedback-container">
        <v-row no-gutters>
          <h5 class="title">{{ $t('feedbackDialog.title') }}</h5>
        </v-row>
        <v-row no-gutters>
          <span class="description">{{ description }}</span>
        </v-row>
        <v-container v-if="step == 0" class="neat-container mt-5">
          <v-row no-gutters align="center" justify="center">
            <v-col align="right">
              <v-icon color="#033e8c" size=64 @click="onThumbUp">mdi-thumb-up</v-icon>
            </v-col>
            <div style="width: 40px"></div>
            <v-col align="left">
              <v-icon color="#033e8c" size=64 @click="onThumbDown">mdi-thumb-down</v-icon>
            </v-col>
          </v-row>
        </v-container>
        <v-container v-else-if="step == 1" class="p-0">
          <v-row no-gutters>
            <v-col align="right">
              <span class="hint">{{ feedbackLength }}</span>
            </v-col>
          </v-row>
          <v-textarea
            outlined
            hide-details
            class="feedback-area"
            color="rgba(224, 231, 255, 1)"
            background-color="rgba(224, 231, 255, 0.2)"
            height=105
            v-model="feedback"
            @click="onTextArea">
          </v-textarea>
          <v-row no-gutters class="guide-row">
            <span v-if="isOnText && feedback.length == 0" class="desc-small text-danger">{{ $t('feedbackDialog.inputGuide') }}</span>
          </v-row>
          <v-spacer></v-spacer>
          <v-row no-gutters>
            <v-col cols="7" class="pr-1">
              <v-btn color="#033e8c" dark block @click="onSkipDescription">{{ $t('feedbackDialog.skipDescription') }}</v-btn>
            </v-col>
            <v-col cols="5" class="pl-1">
              <v-btn color="#d9753b" :dark="feedback.length != 0" block :disabled="feedback.length == 0" @click="onNext">{{ $t('feedbackDialog.next') }}</v-btn>
            </v-col>
          </v-row>
        </v-container>
        <v-container v-else-if="step == 2" class="p-0">
          <v-row no-gutters>
            <v-col class="audio-container" align="center" justify-self="center">
              <vue-record-audio class="audio-control" mode="press" :disabled="isAudioDisabled" @result="onResult" />
              <span v-if="isUploading" class="desc-small">{{ $t('feedbackDialog.uploading') }}</span>
              <v-progress-linear
                v-if="isUploading"
                color="#d9753b"
                background-color="#ffffff"
                width="131px"
                height="11px"
                style="width: 131px"
                :value="uploadProgress">
              </v-progress-linear>
              <span v-if="isSendingFeedback" class="desc-small">{{ $t('feedbackDialog.sending') }}</span>
              <span v-if="isUploadingSuccess" class="desc-small text-dark">{{ $t('feedbackDialog.uploadingDone') }}</span>
            </v-col>
          </v-row>
          <v-row v-if="!isUploading && !isSendingFeedback && !isUploadingSuccess" no-gutters justify="end">
            <v-col cols="7" class="pr-1">
              <v-btn v-if="feedback.length > 0" color="#033e8c" dark block @click="onSendWithoutAudio">{{ $t('feedbackDialog.sendWithoutAudio') }}</v-btn>
            </v-col>
            <v-col cols="5" class="pl-1">
              <v-btn color="#d9753b" :dark="isAudioAvailable" block :disabled="!isAudioAvailable" @click="onSend">{{ $t('feedbackDialog.send') }}</v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-menu>
  </element>
</template>

<script>
export default {
  name: "FeedbackButton",
  props: {
    top: {
      type: [Boolean, String],
      default: false
    },
    left: {
      type: Boolean,
      default: false
    },
    open: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      active: false,
      step: 0,
      sentiment: null,
      closeOnClick: false,
      closeOnContentClick: false,
      feedback: '',
      audio: null,
      uploadProgress: 0,
      isUploading: false,
      isUploadingSuccess: false,
      isSendingFeedback: false,
      isAudioDisabled: false,
      isOnText: false,
      isAudioAvailable: false,
    }
  },
  computed: {
    description: function () {
      return this.$t(`feedbackDialog.description.step-${this.step}`)
    },
    feedbackLength: function () {
      return this.feedback.length + '/300'
    },
  },
  watch: {
    open: function (val) {
      this.active = val
    },
    active: function () {
      if (this.active === true) {
        this.initialize()
      }
    }
  },
  methods: {
    initialize() {
      this.step = 0
      this.sentiment = null
      this.closeOnClick = false
      this.closeOnContentClick = false
      this.feedback = ''
      this.audio = null
      this.uploadProgress = 0
      this.isUploading = false
      this.isUploadingSuccess = false
      this.isSendingFeedback = false
      this.isAudioDisabled = false
      this.isOnText = false
      this.isAudioAvailable = false
    },
    hideFeedbackView() {
      this.active = false
      this.$store.dispatch('hideFeedbackDialog')
    },
    onClick() {
      this.active = !this.active
      this.isAudioDisabled = false
      if (!this.active) {
        this.$store.dispatch('hideFeedbackDialog')
      }
    },
    onThumbUp() {
      this.sentiment = true
      this.step = 1
    },
    onThumbDown() {
      this.sentiment = false
      this.step = 1
    },
    onSkipDescription() {
      this.feedback = ''
      this.step = 2
    },
    onNext() {
      this.step = 2
    },
    onSendWithoutAudio() {
      this.onSend()
    },
    onTextArea() {
      this.isOnText = true
    },
    onResult(data) {
      this.isAudioAvailable = true
      this.audio = data
    },
    closeFeedback(sent) {
      // setTimeout(() => {
        this.active = false
        this.$store.dispatch('hideFeedbackDialog')
        this.$emit('closed', sent)
      // }, 3000)
    },
    onSend() {
      const pid = this.$store.getters.getCurrentPID
      if (pid === null) {
        console.log("This view is not part of process")
        return
      }

      this.isSendingFeedback = true
      this.isAudioDisabled = true

      let formData = new FormData()

      const variables = {}
        
      formData.append('process_instance_id', this.$store.getters.getCurrentPID)
      formData.append('task_definition_key', this.$store.getters.getCurrentTDK)
      formData.append('view_name', this.$store.getters.getCurrentTask.taskName)
      formData.append('sentiments', this.sentiment ? 1 : 0)
      formData.append('variables', JSON.stringify(variables))
      formData.append('feedback_text', this.feedback || '')

      this.axios.post(
        '/Feedbacks/put',
        formData, 
        { withCredentials: true },
      )
      .then(response => {
        const feedbackID = response.data.data.feedback_id

        if (this.audio !== null) {
          let reader = new FileReader()

          reader.onloadend = () => {
            const base64 = reader.result
            const base64Audio = "data:audio/webm;base64," + base64.split(',')[1]
            this.uploadAudio(feedbackID, base64Audio)
          }

          reader.readAsDataURL(this.audio)          
        } else {
          console.log("No audio to upload!")
          this.closeFeedback(true)
        }
      })
      .catch(error => {
        console.log(error)
        this.closeFeedback(false)
      })
    },
    uploadAudio(fid, audioData) {     
      this.isUploading = true
      this.isUploadingSuccess = false
      let formData = new FormData()
        
      formData.append('data_id', fid)
      formData.append('data_doc_raw', audioData)
      formData.append('document_type_id', 5)

      const config = {
        onUploadProgress: function( progressEvent ) {
          this.uploadProgress = parseInt( Math.round((progressEvent.loaded / progressEvent.total) * 100) )
        }
      }

      this.axios.post(
        '/Documents/put', 
        formData, 
        config,
        { withCredentials: true },
      )
      .then(response => {
        console.log(response)
        this.isUploadingSuccess = true
        this.closeFeedback(true)
      })
      .catch(error => {
        console.log(error)
        this.isUploadingSuccess = false
        this.closeFeedback(false)
      })
      .finally(() => {
        this.isUploading = false
      })
    },
    noScroll() {
      window.scrollTo(0, this.previousScrollY)
    },
  }
}
</script>

<style scoped>
.v-menu__content {
  border-radius: 12px!important;
}

.feedback-container {
  height: 339px;
  border-radius: 12px!important;
  z-index: 4;
}

.title {
  font-size: 1.5rem;
  font-weight: 700;
}

.description {
  font-size: 1.125rem;
  font-weight: 300;
  margin-top: 1.125rem;
}

.desc-small {
  font-size: 0.875rem;
  font-weight: 300;
  color: #d9753b;
}

.v-progress-linear {
  border: 1px solid #d9753b;
}

.hint {
  font-size: 0.688rem;
  font-weight: 100;
  color: #2e384d;
}

.guide-row {
  height: 4rem;
}

.audio-container {
  height: 11rem;
}

.audio-control {
  margin-top: 2rem;
}

.v-input .v-text-field .feedback-area {
  caret-color: black!important;
}
</style>